import { Link } from 'react-router-dom'

import { styled } from 'styled-system/jsx'

const PageStack = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
})

const BackLink = styled(Link, {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    textDecoration: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingX: '4px',
    _hover: {
      color: 'quickpass.primary',
    },
  },
})

const GalleryRoot = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '8px',
  },
})

const MainImageFrame = styled('div', {
  base: {
    position: 'relative',
    aspectRatio: '16 / 9',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 'lg',
    backgroundColor: '#F1ECFF',
    boxShadow: '0 24px 48px -24px rgba(128, 84, 255, 0.32)',
  },
})

const MainImage = styled('img', {
  base: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
})

const ImagePlaceholder = styled('div', {
  base: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '64px',
    color: 'quickpass.primary',
    opacity: '0.6',
  },
})

const ThumbnailRow = styled('div', {
  base: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    paddingY: '4px',
  },
})

const Thumbnail = styled('button', {
  base: {
    flexShrink: '0',
    width: '92px',
    height: '64px',
    overflow: 'hidden',
    borderRadius: 'sm',
    border: '2px solid transparent',
    backgroundColor: '#F1ECFF',
    cursor: 'pointer',
    padding: '0',
    transition: 'border-color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primary',
    },
  },
  variants: {
    isActive: {
      true: { borderColor: 'quickpass.primary' },
    },
  },
})

const ThumbnailImage = styled('img', {
  base: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
})

const HeaderRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
})

const HeaderInfo = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: '1',
    minWidth: '0',
  },
})

const Title = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '32px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.8px',
    lineHeight: '1.15',
  },
})

const Address = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
  },
})

const HeaderActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: '0',
  },
})

const StatusBadge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '28px',
    paddingX: '12px',
    borderRadius: 'pill',
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  variants: {
    tone: {
      available: { backgroundColor: '#D7F5E1', color: '#10663A' },
      'available-soon': { backgroundColor: '#FFF1CC', color: '#7A5300' },
      occupied: { backgroundColor: '#FFE3DD', color: '#8B1E2C' },
      unavailable: { backgroundColor: '#E5E0F5', color: '#4F4666' },
    },
  },
})

const PrimaryRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '14px',
  },
})

const PriceBlock = styled('div', {
  base: {
    fontFamily: 'display',
    fontSize: '28px',
    fontWeight: '700',
    color: 'quickpass.primary',
    letterSpacing: '-0.5px',
  },
})

const MetaPills = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
})

const MetaPill = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingX: '12px',
    paddingY: '6px',
    borderRadius: 'pill',
    backgroundColor: '#F1ECFF',
    color: 'quickpass.primary',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
  },
})

const SectionCard = styled('section', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '20px 22px',
    boxShadow: '0 12px 32px -16px rgba(128, 84, 255, 0.12)',
  },
})

const SectionTitle = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.3px',
  },
})

const Description = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
  },
})

const TagGrid = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
})

const TagChip = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingX: '10px',
    paddingY: '5px',
    borderRadius: 'pill',
    backgroundColor: '#FAF7FF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.ink',
  },
})

const StickyInquireBar = styled('div', {
  base: {
    position: 'sticky',
    bottom: '16px',
    zIndex: '40',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '14px 18px',
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    boxShadow: '0 24px 48px -16px rgba(128, 84, 255, 0.28)',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
  },
})

const InquireSummary = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const InquireSummaryTitle = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const InquireSummaryMeta = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const InquireActions = styled('div', {
  base: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
})

const ModalBackdrop = styled('div', {
  base: {
    position: 'fixed',
    inset: '0',
    zIndex: '60',
    backgroundColor: 'rgba(26, 19, 48, 0.55)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
})

const ModalCard = styled('div', {
  base: {
    width: '100%',
    maxWidth: '520px',
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 24px 60px -16px rgba(0, 0, 0, 0.32)',
  },
})

const ModalTitle = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '22px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.5px',
  },
})

const ModalSubtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.5',
  },
})

const ModalTextarea = styled('textarea', {
  base: {
    width: '100%',
    minHeight: '140px',
    resize: 'vertical',
    fontFamily: 'body',
    fontSize: '14px',
    lineHeight: '1.5',
    padding: '12px 14px',
    borderRadius: 'sm',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    outline: 'none',
    color: 'quickpass.ink',
    _focus: { borderColor: 'quickpass.primary' },
    _placeholder: { color: '#B8B0CC', opacity: '1' },
  },
})

const ModalFooter = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '4px',
  },
})

const ModalError = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: '#8B1E2C',
    margin: '0',
  },
})

const InquireSuccess = styled('div', {
  base: {
    backgroundColor: '#D7F5E1',
    color: '#10663A',
    padding: '14px 16px',
    borderRadius: 'sm',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
  },
})

const SimilarGrid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '12px',
  },
})

const UnitList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const UnitRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#FAF7FF',
    borderRadius: 'sm',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    flexWrap: 'wrap',
  },
})

const UnitInfo = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    flex: '1',
    minWidth: '0',
  },
})

const UnitTitle = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const UnitMeta = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    margin: '0',
  },
})

const UnitPrice = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.primary',
    margin: '0',
  },
})

const UnitActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
})

const UnitStatusBadge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '22px',
    paddingX: '10px',
    borderRadius: 'pill',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  variants: {
    tone: {
      available: { backgroundColor: '#D7F5E1', color: '#10663A' },
      'available-soon': { backgroundColor: '#FFF1CC', color: '#7A5300' },
      occupied: { backgroundColor: '#FFE3DD', color: '#8B1E2C' },
      unavailable: { backgroundColor: '#E5E0F5', color: '#4F4666' },
    },
  },
})

const NotFoundCard = styled('div', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '40px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
})

export {
  PageStack,
  BackLink,
  GalleryRoot,
  MainImageFrame,
  MainImage,
  ImagePlaceholder,
  ThumbnailRow,
  Thumbnail,
  ThumbnailImage,
  HeaderRow,
  HeaderInfo,
  Title,
  Address,
  HeaderActions,
  StatusBadge,
  PrimaryRow,
  PriceBlock,
  MetaPills,
  MetaPill,
  SectionCard,
  SectionTitle,
  Description,
  TagGrid,
  TagChip,
  StickyInquireBar,
  InquireSummary,
  InquireSummaryTitle,
  InquireSummaryMeta,
  InquireActions,
  ModalBackdrop,
  ModalCard,
  ModalTitle,
  ModalSubtitle,
  ModalTextarea,
  ModalFooter,
  ModalError,
  InquireSuccess,
  SimilarGrid,
  UnitList,
  UnitRow,
  UnitInfo,
  UnitTitle,
  UnitMeta,
  UnitPrice,
  UnitActions,
  UnitStatusBadge,
  NotFoundCard,
}
